import { useState, useEffect, useRef } from 'react'

const NAMESPACE = 'bp:page:'

function readFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.sessionStorage.getItem(NAMESPACE + key)
    if (raw === null) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function writeToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(NAMESPACE + key, JSON.stringify(value))
  } catch {
    // ignore quota / privacy-mode errors
  }
}

/**
 * Drop-in useState replacement that persists to sessionStorage so that
 * navigating away from a page and back restores the user's last query inputs.
 * Scope is per browser tab; cleared when the tab closes.
 */
export function usePersistedState<T>(
  key: string,
  initial: T
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => readFromStorage(key, initial))
  const keyRef = useRef(key)

  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key
      setValue(readFromStorage(key, initial))
      return
    }
    writeToStorage(key, value)
  }, [key, value])

  return [value, setValue]
}
